import React, { useEffect, useMemo, useState } from "react";
import { Link, Navigate } from "react-router-dom";
import { api, money, fmtDate, onlyDigits } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { toast } from "sonner";
import { Calendar as CalIcon, CheckCircle2, MessageCircle, MapPin, Phone, Wallet,
  AlertTriangle, Loader2, ChevronLeft, ChevronRight, Users, MapPinned,
  CalendarClock,
} from "lucide-react";
import Money from "@/components/Money";

const DIAS_SHORT = ["SEG", "TER", "QUA", "QUI", "SEX", "SAB", "DOM"];
const DIAS_FULL = ["Segunda", "Terça", "Quarta", "Quinta", "Sexta", "Sábado", "Domingo"];
const METODO_LABEL = { dinheiro: "💵 Dinheiro", pix: "⚡ Pix", credito: "💳 Crédito", debito: "💳 Débito" };

function addDays(iso, n) {
  const d = new Date(iso + "T00:00:00");
  d.setDate(d.getDate() + n);
  return d.toISOString().slice(0, 10);
}

export default function MinhaRota() {
  const { session, isAdmin, isCobrador } = useAuth();
  const todayIso = new Date().toISOString().slice(0, 10);
  const [data, setData] = useState(todayIso);
  const [agenda, setAgenda] = useState(null);
  const [loading, setLoading] = useState(true);
  const [rotaFiltro, setRotaFiltro] = useState("todas"); // "todas" | rota_id | "sem-rota"
  const [modal, setModal] = useState(null);
  const [modalValor, setModalValor] = useState("");
  const [modalMetodo, setModalMetodo] = useState("dinheiro");
  const [saving, setSaving] = useState(false);
  // Remarcação
  const [remModal, setRemModal] = useState(null);
  const [remData, setRemData] = useState("");
  const [remMotivo, setRemMotivo] = useState("");

  const canAccess = isAdmin || isCobrador;

  const load = async () => {
    if (!canAccess) return;
    setLoading(true);
    try {
      const r = await api.get("/minha-rota", { params: { data } });
      setAgenda(r.data);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao carregar rota");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, [data, canAccess]);

  const parcelas = useMemo(() => {
    if (!agenda?.parcelas) return [];
    if (rotaFiltro === "todas") return agenda.parcelas;
    if (rotaFiltro === "sem-rota") return agenda.parcelas.filter((p) => !p.rota_id);
    return agenda.parcelas.filter((p) => p.rota_id === rotaFiltro);
  }, [agenda, rotaFiltro]);

  // Agrupa por cliente
  const grupos = useMemo(() => {
    const map = new Map();
    for (const p of parcelas) {
      if (!map.has(p.cliente_id)) {
        map.set(p.cliente_id, {
          cliente_id: p.cliente_id,
          cliente_nome: p.cliente_nome,
          cliente_telefone: p.cliente_telefone,
          cliente_bairro: p.cliente_bairro,
          cliente_endereco: p.cliente_endereco,
          rota_nome: p.rota_nome,
          rota_dia_semana: p.rota_dia_semana,
          parcelas: [],
        });
      }
      map.get(p.cliente_id).parcelas.push(p);
    }
    return Array.from(map.values());
  }, [parcelas]);

  const resumoFiltrado = useMemo(() => {
    const previsto = parcelas.reduce((a, p) => a + p.valor, 0);
    const recebido = parcelas.reduce((a, p) => a + p.valor_pago, 0);
    const pagas = parcelas.filter((p) => p.status === "paga").length;
    return {
      total_previsto: previsto,
      total_recebido: recebido,
      total_pendente: previsto - recebido,
      parcelas_total: parcelas.length,
      parcelas_pagas: pagas,
    };
  }, [parcelas]);

  // Permissão: só cobrador ou admin (depois dos hooks pra manter ordem)
  if (!canAccess) return <Navigate to="/clientes" replace />;

  const openBaixa = (p) => {
    setModal(p);
    setModalValor(String((p.valor - p.valor_pago).toFixed(2)));
    setModalMetodo(p.metodo_pagamento || "dinheiro");
  };

  const openRemarcar = (p) => {
    setRemModal(p);
    // Default: amanhã
    const amanha = new Date(data + "T00:00:00");
    amanha.setDate(amanha.getDate() + 1);
    setRemData(amanha.toISOString().slice(0, 10));
    setRemMotivo("");
  };

  const confirmRemarcar = async () => {
    const motivo = (remMotivo || "").trim();
    if (!motivo) return toast.error("Informe o motivo da remarcação");
    if (!remData) return toast.error("Escolha a nova data");
    if (remData === (remModal.data_cobranca || remModal.vencimento)) {
      return toast.error("A nova data é igual à atual");
    }
    setSaving(true);
    try {
      await api.post(`/vendas/${remModal.venda_id}/parcelas/${remModal.parcela_id}/remarcar`, {
        nova_data: remData,
        motivo,
      });
      toast.success("Cobrança remarcada");
      // Abre WhatsApp automaticamente se cliente tiver telefone
      const phone = onlyDigits(remModal.cliente_telefone);
      if (phone) {
        try {
          const t = await api.get("/whatsapp/template", {
            params: {
              tipo: "remarcacao",
              cliente: remModal.cliente_nome,
              parcela: `${remModal.parcela_numero}/${remModal.total_parcelas}`,
              valor: remModal.valor - remModal.valor_pago,
              nova_data: remData,
              motivo,
            },
          });
          window.open(`https://wa.me/${phone}?text=${encodeURIComponent(t.data.mensagem)}`, "_blank");
        } catch (_e) {
          // fallback: mensagem default
          const dataBr = remData.split("-").reverse().join("/");
          const msg = `Olá ${remModal.cliente_nome}! Sua parcela ${remModal.parcela_numero}/${remModal.total_parcelas} foi remarcada para ${dataBr}. Qualquer dúvida me avisa! 🙂`;
          window.open(`https://wa.me/${phone}?text=${encodeURIComponent(msg)}`, "_blank");
        }
      }
      setRemModal(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro ao remarcar");
    } finally {
      setSaving(false);
    }
  };

  const confirmBaixa = async () => {
    const v = parseFloat(modalValor);
    if (!(v > 0)) return toast.error("Valor inválido");
    setSaving(true);
    try {
      await api.post(`/vendas/${modal.venda_id}/parcelas/${modal.parcela_id}/pagar`, {
        valor_pago: v, metodo_pagamento: modalMetodo,
      });
      // Mensagem WhatsApp opcional
      const phone = onlyDigits(modal.cliente_telefone);
      if (phone) {
        const resta = Math.max(0, (modal.valor - modal.valor_pago) - v);
        try {
          const t = await api.get("/whatsapp/template", {
            params: {
              tipo: "recebido",
              cliente: modal.cliente_nome,
              parcela: `${modal.parcela_numero}/${modal.total_parcelas}`,
              valor: v,
              resta,
              metodo: modalMetodo,
            },
          });
          window.open(`https://wa.me/${phone}?text=${encodeURIComponent(t.data.mensagem)}`, "_blank");
        } catch (_e) { /* ignore */ }
      }
      toast.success("Baixa registrada");
      setModal(null);
      load();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Erro");
    } finally {
      setSaving(false);
    }
  };

  const isToday = data === todayIso;
  const dataLabel = (() => {
    const d = new Date(data + "T00:00:00");
    const wd = (d.getDay() + 6) % 7;
    return `${DIAS_FULL[wd]}, ${fmtDate(data)}`;
  })();

  return (
    <div>
      <header className="mb-4">
        <div className="text-xs font-bold uppercase tracking-wider text-[color:var(--terracotta)] inline-flex items-center gap-1">
          <MapPinned className="w-3.5 h-3.5" /> Minha Rota
          {!isCobrador && isAdmin && <span className="ml-1 text-stone-500">(visão admin)</span>}
        </div>
        <h1 className="font-display text-3xl font-extrabold tracking-tight text-stone-900">
          {isToday ? "Rota de hoje" : "Rota do dia"}
        </h1>
        <p className="text-sm text-stone-500 mt-1">
          {session?.staff?.name && !isAdmin ? `${session.staff.name} • ` : ""}{dataLabel}
        </p>
      </header>

      <div className="carne-card p-3 mb-3 flex items-center gap-2" data-testid="rota-date-nav">
        <button
          data-testid="btn-prev-day"
          onClick={() => setData(addDays(data, -1))}
          className="h-10 w-10 rounded-full border border-stone-200 bg-white text-stone-600 inline-flex items-center justify-center hover:bg-stone-50"
        >
          <ChevronLeft className="w-4 h-4" />
        </button>
        <div className="flex-1">
          <input
            data-testid="input-rota-date"
            type="date"
            value={data}
            onChange={(e) => setData(e.target.value)}
            className="w-full h-10 px-3 rounded-lg border border-stone-200 bg-white text-center font-mono-num"
          />
        </div>
        <button
          data-testid="btn-next-day"
          onClick={() => setData(addDays(data, 1))}
          className="h-10 w-10 rounded-full border border-stone-200 bg-white text-stone-600 inline-flex items-center justify-center hover:bg-stone-50"
        >
          <ChevronRight className="w-4 h-4" />
        </button>
        {!isToday && (
          <button
            data-testid="btn-today"
            onClick={() => setData(todayIso)}
            className="h-10 px-3 rounded-lg bg-[color:var(--terracotta)] text-white text-xs font-bold inline-flex items-center gap-1"
          >
            <CalIcon className="w-3.5 h-3.5" /> Hoje
          </button>
        )}
      </div>

      {loading && <div className="text-stone-500 text-sm">Carregando…</div>}

      {!loading && agenda && (
        <>
          {/* Filtro de rotas quando o cobrador tem múltiplas rotas no dia */}
          {agenda.rotas_do_dia.length > 1 && (
            <div className="mb-3 flex flex-wrap gap-2" data-testid="rota-filter-pills">
              <button
                data-testid="btn-rota-filter-todas"
                onClick={() => setRotaFiltro("todas")}
                className={`h-8 px-3 rounded-full text-xs font-bold ${rotaFiltro === "todas" ? "bg-[color:var(--terracotta)] text-white" : "bg-white border border-stone-200 text-stone-700"}`}
              >
                Todas ({agenda.parcelas.length})
              </button>
              {agenda.rotas_do_dia.map((r) => {
                const count = agenda.parcelas.filter((p) => p.rota_id === r.id).length;
                return (
                  <button
                    key={r.id}
                    data-testid={`btn-rota-filter-${r.id}`}
                    onClick={() => setRotaFiltro(r.id)}
                    className={`h-8 px-3 rounded-full text-xs font-bold inline-flex items-center gap-1 ${rotaFiltro === r.id ? "bg-[color:var(--terracotta)] text-white" : "bg-white border border-stone-200 text-stone-700"}`}
                  >
                    {r.nome} ({DIAS_SHORT[r.dia_semana]}) · {count}
                  </button>
                );
              })}
            </div>
          )}

          <div className="grid grid-cols-3 gap-2 mb-4" data-testid="rota-resumo">
            <div className="rounded-xl bg-white border border-stone-200 p-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-stone-500 flex items-center gap-1">
                <Wallet className="w-3 h-3" /> Previsto
              </div>
              <div className="font-mono-num text-base font-bold text-stone-900"><Money v={resumoFiltrado.total_previsto} /></div>
            </div>
            <div className="rounded-xl bg-emerald-50 border border-emerald-200 p-3">
              <div className="text-[10px] font-bold uppercase tracking-wider text-emerald-700 flex items-center gap-1">
                <CheckCircle2 className="w-3 h-3" /> Recebido
              </div>
              <div className="font-mono-num text-base font-bold text-emerald-900"><Money v={resumoFiltrado.total_recebido} /></div>
            </div>
            <div className={`rounded-xl p-3 border ${resumoFiltrado.total_pendente > 0 ? "bg-rose-50 border-rose-200" : "bg-stone-50 border-stone-200"}`}>
              <div className={`text-[10px] font-bold uppercase tracking-wider flex items-center gap-1 ${resumoFiltrado.total_pendente > 0 ? "text-rose-700" : "text-stone-500"}`}>
                <AlertTriangle className="w-3 h-3" /> Pendente
              </div>
              <div className={`font-mono-num text-base font-bold ${resumoFiltrado.total_pendente > 0 ? "text-rose-900" : "text-stone-900"}`}><Money v={resumoFiltrado.total_pendente} /></div>
            </div>
          </div>

          <div className="text-xs text-stone-500 mb-3 inline-flex items-center gap-1">
            <Users className="w-3 h-3" /> {grupos.length} {grupos.length === 1 ? "cliente" : "clientes"} · {resumoFiltrado.parcelas_pagas}/{resumoFiltrado.parcelas_total} parcelas pagas
          </div>

          {grupos.length === 0 && (
            <div className="carne-card p-8 text-center text-stone-500 text-sm" data-testid="rota-empty">
              {isToday ? "Nenhum cliente pra cobrar hoje. Dia tranquilo ☕" : `Nenhum cliente pra cobrar em ${fmtDate(data)}.`}
            </div>
          )}

          <div className="space-y-3" data-testid="rota-grupos">
            {grupos.map((g) => (
              <div key={g.cliente_id} className="carne-card p-4" data-testid={`rota-cliente-${g.cliente_id}`}>
                <div className="flex items-start justify-between gap-2 mb-2">
                  <div className="min-w-0 flex-1">
                    <Link to={`/clientes/${g.cliente_id}`} className="font-display text-base font-bold text-stone-900 hover:underline block truncate">
                      {g.cliente_nome}
                    </Link>
                    <div className="text-[11px] text-stone-500 flex items-center gap-2 flex-wrap mt-0.5">
                      {g.cliente_bairro && (
                        <span className="inline-flex items-center gap-1"><MapPin className="w-3 h-3" />{g.cliente_bairro}{g.cliente_endereco ? ` — ${g.cliente_endereco}` : ""}</span>
                      )}
                      {g.rota_nome && (
                        <span className="inline-flex items-center gap-1 text-[color:var(--terracotta)] font-bold">
                          <MapPinned className="w-3 h-3" /> {g.rota_nome} ({DIAS_SHORT[g.rota_dia_semana]})
                        </span>
                      )}
                    </div>
                  </div>
                  {g.cliente_telefone && (
                    <a
                      href={`https://wa.me/${onlyDigits(g.cliente_telefone)}`}
                      target="_blank" rel="noreferrer"
                      className="h-10 w-10 rounded-full bg-[color:var(--whatsapp-light)] text-[color:var(--whatsapp-dark)] inline-flex items-center justify-center shrink-0"
                      data-testid={`btn-whats-${g.cliente_id}`}
                      title="Abrir WhatsApp"
                    >
                      <MessageCircle className="w-4 h-4" />
                    </a>
                  )}
                </div>
                <div className="divide-y divide-stone-100">
                  {g.parcelas.map((p) => (
                    <div key={p.parcela_id} className="py-2 flex items-center justify-between gap-2" data-testid={`rota-parcela-${p.parcela_id}`}>
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 flex-wrap">
                          <span className="text-sm font-semibold text-stone-900">Parc. {p.parcela_numero}/{p.total_parcelas}</span>
                          {p.status === "paga" && <span className="stamp-paid">Paga</span>}
                          {p.status === "parcial" && <span className="text-[10px] font-bold uppercase tracking-wider text-amber-700 bg-amber-100 px-2 py-0.5 rounded-full">Parcial</span>}
                          {p.status === "atrasada" && <span className="stamp-overdue">Atrasada</span>}
                          {p.metodo_pagamento && (
                            <span className="text-[10px] font-bold uppercase tracking-wider text-stone-600 bg-stone-100 px-1.5 py-0.5 rounded">
                              {METODO_LABEL[p.metodo_pagamento]}
                            </span>
                          )}
                        </div>
                        <div className="text-[11px] text-stone-500 truncate">
                          {p.descricao} • Vence {fmtDate(p.vencimento)}
                        </div>
                        <div className="text-xs text-stone-700 mt-0.5 font-mono-num">
                          <Money v={p.valor_pago} /> / <Money v={p.valor} />
                        </div>
                      </div>
                      {p.status !== "paga" && (
                        <div className="flex flex-col gap-1 shrink-0">
                          <button
                            data-testid={`btn-rota-baixa-${p.parcela_id}`}
                            onClick={() => openBaixa(p)}
                            className="h-9 px-3 rounded-full bg-emerald-600 text-white text-xs font-bold inline-flex items-center gap-1 hover:bg-emerald-700"
                          >
                            <CheckCircle2 className="w-3.5 h-3.5" /> Baixar
                          </button>
                          <button
                            data-testid={`btn-rota-remarcar-${p.parcela_id}`}
                            onClick={() => openRemarcar(p)}
                            className="h-8 px-3 rounded-full bg-white border border-amber-300 text-amber-800 text-[11px] font-bold inline-flex items-center gap-1 hover:bg-amber-50"
                          >
                            <CalendarClock className="w-3 h-3" /> Remarcar
                          </button>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </>
      )}

      {modal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={() => setModal(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="rota-modal">
            <h3 className="font-display text-lg font-bold text-stone-900 mb-1">Dar baixa na parcela {modal.parcela_numero}</h3>
            <p className="text-sm text-stone-500 mb-3">
              {modal.cliente_nome} • Em aberto: <Money v={modal.valor - modal.valor_pago} />
            </p>
            <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Valor recebido (R$)</label>
            <input
              data-testid="rota-modal-valor"
              type="number"
              step="0.01"
              min="0"
              value={modalValor}
              onChange={(e) => setModalValor(e.target.value)}
              autoFocus
              className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white outline-none focus:border-[color:var(--terracotta)]"
            />
            <div className="mt-3">
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Forma de pagamento</label>
              <div className="mt-1 grid grid-cols-2 gap-2">
                {[
                  { k: "dinheiro", label: "Dinheiro", emoji: "💵" },
                  { k: "pix", label: "Pix", emoji: "⚡" },
                  { k: "credito", label: "Crédito", emoji: "💳" },
                  { k: "debito", label: "Débito", emoji: "💳" },
                ].map(({ k, label, emoji }) => (
                  <button
                    key={k}
                    type="button"
                    data-testid={`rota-modal-method-${k}`}
                    onClick={() => setModalMetodo(k)}
                    className={`h-11 rounded-xl border-2 font-bold text-sm inline-flex items-center justify-center gap-1 transition-colors ${
                      modalMetodo === k
                        ? "border-[color:var(--terracotta)] bg-[color:var(--terracotta-light)] text-[color:var(--terracotta)]"
                        : "border-stone-200 bg-white text-stone-700 hover:bg-stone-50"
                    }`}
                  >
                    <span>{emoji}</span> {label}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setModal(null)} className="flex-1 h-12 rounded-xl border border-stone-200 font-bold text-stone-700">Cancelar</button>
              <button data-testid="rota-modal-confirm" onClick={confirmBaixa} disabled={saving} className="btn-primary flex-1">
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CheckCircle2 className="w-4 h-4" />}
                Confirmar
              </button>
            </div>
          </div>
        </div>
      )}
      {remModal && (
        <div className="fixed inset-0 z-50 bg-black/50 flex items-end sm:items-center justify-center p-4" onClick={() => setRemModal(null)}>
          <div className="bg-white rounded-2xl p-5 w-full max-w-md" onClick={(e) => e.stopPropagation()} data-testid="rota-rem-modal">
            <h3 className="font-display text-lg font-bold text-stone-900 mb-1 inline-flex items-center gap-1">
              <CalendarClock className="w-5 h-5 text-amber-700" /> Remarcar cobrança
            </h3>
            <p className="text-sm text-stone-500 mb-3">
              {remModal.cliente_nome} • Parc. {remModal.parcela_numero}/{remModal.total_parcelas} • <Money v={remModal.valor - remModal.valor_pago} />
            </p>
            <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Nova data da cobrança*</label>
            <input
              data-testid="rota-rem-data"
              type="date"
              value={remData}
              onChange={(e) => setRemData(e.target.value)}
              className="mt-1 w-full h-12 px-3 rounded-xl border border-stone-200 bg-white"
            />
            <div className="mt-3">
              <label className="text-xs font-bold uppercase tracking-wider text-stone-500">Motivo da remarcação*</label>
              <textarea
                data-testid="rota-rem-motivo"
                value={remMotivo}
                onChange={(e) => setRemMotivo(e.target.value)}
                placeholder="Ex: Cliente estava ausente / pediu pra pagar sexta / imprevisto"
                className="mt-1 w-full min-h-[90px] p-3 rounded-xl border border-stone-200 bg-white"
                required
              />
              <div className="flex flex-wrap gap-1 mt-1" data-testid="rota-rem-sugestoes">
                {[
                  "Cliente estava ausente",
                  "Cliente pediu para pagar outro dia",
                  "Cliente teve um imprevisto",
                  "Cliente solicitou mudança de data",
                ].map((s) => (
                  <button
                    key={s}
                    type="button"
                    onClick={() => setRemMotivo(s)}
                    className="text-[10px] px-2 py-1 rounded-full bg-stone-100 text-stone-600 hover:bg-stone-200"
                  >
                    {s}
                  </button>
                ))}
              </div>
            </div>
            <div className="flex gap-2 mt-4">
              <button onClick={() => setRemModal(null)} className="flex-1 h-12 rounded-xl border border-stone-200 font-bold text-stone-700">Cancelar</button>
              <button
                data-testid="rota-rem-confirm"
                onClick={confirmRemarcar}
                disabled={saving || !(remMotivo || "").trim() || !remData}
                className="btn-primary flex-1"
                style={{ background: "#C85A32" }}
              >
                {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <CalendarClock className="w-4 h-4" />}
                Confirmar remarcação
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
